import { alpha, IconButton, IconButtonProps, Stack, styled } from '@mui/material';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { ReactionSummary } from './group-reactions';
import { ReactionChip } from './reaction-chip';

const ADD_REACTION_ICON_SIZE_PX = 16;

// Bare until hovered: no border and no fill at rest.
const AddButton = styled(IconButton)(({ theme }) => {
  const { sys } = theme.palette.custom.OV;
  return {
    padding: theme.spacing(0.375),
    color: sys.onSurfaceVariant,
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: alpha(sys.onSurface, theme.palette.action.selectedOpacity),
      color: sys.onSurface,
    },
  };
});

export const AddReactionButton = (props: Omit<IconButtonProps, 'children'>) => {
  return (
    <Tooltip title="Add reaction">
      <AddButton aria-label="Add reaction" size="small" {...props}>
        <DynamicIcon library="lucide" name="smile-plus" size={ADD_REACTION_ICON_SIZE_PX} />
      </AddButton>
    </Tooltip>
  );
};

export interface MessageReactionsProps {
  reactions: ReactionSummary[];
  /** Whether the viewer can add or remove reactions; otherwise chips are read-only. */
  canReact: boolean;
  onToggle: (emoji: string, reactedByMe: boolean) => void;
  onAddClick: (anchor: HTMLElement) => void;
  /** Shown in every chip's tooltip, e.g. that only the team sees reactions. */
  notice?: string;
}

/**
 * A message's reaction chips followed by an always-visible add button. Renders nothing until
 * the message has a reaction; the first one is added from the message toolbar.
 */
export const MessageReactions = ({
  reactions,
  canReact,
  onToggle,
  onAddClick,
  notice,
}: MessageReactionsProps) => {
  if (!reactions.length) {
    return null;
  }

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      alignItems="center"
      role="group"
      aria-label="Reactions"
      sx={(theme) => ({ gap: theme.spacing(0.5) })}
    >
      {reactions.map((reaction) => (
        <ReactionChip
          key={reaction.emoji}
          reaction={reaction}
          isInteractive={canReact}
          notice={notice}
          onToggle={() => onToggle(reaction.emoji, reaction.reactedByMe)}
        />
      ))}
      {canReact ? (
        <AddReactionButton onClick={(event) => onAddClick(event.currentTarget)} />
      ) : null}
    </Stack>
  );
};
